'use client';

import { ReactNode } from 'react';

import { KeyboardArrowDownSharp } from '@mui/icons-material';
import {
  Box,
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material';
import { deepmerge } from '@mui/utils';

type NerdWalletColor = Partial<{
  lightest: string;
  extraLight: string;
  lighter2: string;
  lighter: string;
  light: string;
  primary: string;
  dark: string;
  darker: string;
  darker2: string;
  darkest: string;
  bone: string;
}>;

export enum FontFamily {
  GOTHAM = `Gotham,system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif`,
  CHRONICLE_DISPLAY = `Chronicle Display,serif`,
}

enum NerdWalletBlue {
  LIGHTEST = '#EEF7FF',
  EXTRA_LIGHT = '#DEEFFF',
  LIGHTER_2 = '#C8E4FE',
  LIGHTER = '#9DCDFA',
  LIGHT = '#54A0E8',
  PRIMARY = '#005FB9',
  DARK = '#02427E',
  DARKEST = '#02143A',
}

enum NerdWalletGreen {
  EXTRA_LIGHT = '#EFFCF8',
  LIGHTEST = '#E3FAF2',
  LIGHTER_2 = '#B7ECDA',
  LIGHTER = '#81D6B8',
  LIGHT = '#40AD87',
  PRIMARY = '#008255',
  DARK = '#006642',
  DARKEST = '#00482F',
}

enum NerdWalletLime {
  LIGHT = '#E8FDAC',
  PRIMARY = '#E2FF39',
  DARK = '#B2DD6D',
}

enum NerdWalletYellow {
  EXTRA_LIGHT = '#FFFBF4',
  LIGHTEST = '#FFF7EA',
  LIGHTER_2 = '#FFF0D6',
  LIGHTER = '#FFE2AE',
  LIGHT = '#FBD083',
  PRIMARY = '#FFC764',
  DARK = '#EFA92E',
  DARKEST = '#B07000',
}

enum NerdWalletRed {
  EXTRA_LIGHT = '#FFF7F7',
  LIGHTEST = '#FFE8E8',
  LIGHTER_2 = '#FFBFBF',
  LIGHTER = '#FF9090',
  LIGHT = '#FF6868',
  PRIMARY = '#DE2828',
  DARK = '#B40000',
  DARKEST = '#7A0000',
}

enum NerdWalletBrown {
  EXTRA_LIGHT = '#FFFAF9',
  LIGHTEST = '#FEF4F1',
  LIGHTER_2 = '#FFEAE2',
  LIGHTER = '#EDBEAD',
  LIGHT = '#D58B70',
  PRIMARY = '#B96C4F',
  DARK = '#91472B',
  DARKEST = '#652812',
}

enum NerdWalletNeutral {
  DARKEST = '#000000',
  DARKER_2 = '#3D4045',
  DARKER = '#64666A',
  DARK = '#8B8C8F',
  LIGHT = '#B1B3B5',
  LIGHTER = '#D8D9DA',
  LIGHTER_2 = '#EBEBEC',
  LIGHTEST = '#F5F5F5',
  WHITE = '#FFFFFF',
  BONE = '#F4EEE6',
}

enum NerdWalletLogo {
  DARK = '#00682C',
  PRIMARY = '#00AE4D',
  LIGHT = '#8DC63F',
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    title: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleExtraLarge: React.CSSProperties;
    titleSmall: React.CSSProperties;
    textLarge: React.CSSProperties;
    textExtraLarge: React.CSSProperties;
    text: React.CSSProperties;
    textSmall: React.CSSProperties;
    textExtraSmall: React.CSSProperties;
    accent: React.CSSProperties;
    accentSmall: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleExtraLarge?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    textLarge?: React.CSSProperties;
    textExtraLarge?: React.CSSProperties;
    text?: React.CSSProperties;
    textSmall?: React.CSSProperties;
    textExtraSmall?: React.CSSProperties;
    accent?: React.CSSProperties;
    accentSmall?: React.CSSProperties;
  }

  interface PaletteOptions {
    blue: NerdWalletColor;
    brown: NerdWalletColor;
    green: NerdWalletColor;
    lime: NerdWalletColor;
    neutral: NerdWalletColor;
    red: NerdWalletColor;
    yellow: NerdWalletColor;
    logo: NerdWalletColor;
  }

  interface Palette {
    blue: NerdWalletColor;
    brown: NerdWalletColor;
    green: NerdWalletColor;
    lime: NerdWalletColor;
    neutral: NerdWalletColor;
    red: NerdWalletColor;
    yellow: NerdWalletColor;
    logo: NerdWalletColor;
  }

  // Most of our designs can be achieved using 3 screen sizes - mobile: sm, tablet: md and desktop: lg
  // Width values apply to all wider breakpoints, and larger breakpoints override those given to smaller breakpoints.
  // For example if you want your lg to behavior to be the same as md there is no need to specify lg.
  interface BreakpointOverrides {
    xs: false;
    sm: true;
    md: true;
    lg: true;
    xl: false;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    blue: true;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1: true;
    body2: true;
    button: true;
    caption: true;
    overline: true;
    subtitle1: true;
    subtitle2: true;

    title: true;
    titleLarge: true;
    titleExtraLarge: true;
    titleSmall: true;

    textExtraSmall: true;
    textSmall: true;
    text: true;
    textLarge: true;
    textExtraLarge: true;

    accent: true;
    accentSmall: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    tertiary: true;
    nwPlus: true;
    link: true;
  }
}

export const { breakpoints, palette, shadows: defaultShadows } = createTheme();

Object.assign(palette, {
  text: {
    primary: NerdWalletNeutral.DARKEST,
    secondary: NerdWalletNeutral.DARKER,
  },
  primary: {
    dark: NerdWalletGreen.DARK,
    main: NerdWalletGreen.PRIMARY,
    light: NerdWalletGreen.LIGHT,
  },
  secondary: {
    light: NerdWalletBlue.LIGHTEST,
    main: NerdWalletBlue.PRIMARY,
  },
  blue: {
    lightest: NerdWalletBlue.LIGHTEST,
    extraLight: NerdWalletBlue.EXTRA_LIGHT,
    lighter2: NerdWalletBlue.LIGHTER_2,
    lighter: NerdWalletBlue.LIGHTER,
    light: NerdWalletBlue.LIGHT,
    primary: NerdWalletBlue.PRIMARY,
    dark: NerdWalletBlue.DARK,
    darkest: NerdWalletBlue.DARKEST,
  },
  green: {
    lightest: NerdWalletGreen.LIGHTEST,
    extraLight: NerdWalletGreen.EXTRA_LIGHT,
    lighter2: NerdWalletGreen.LIGHTER_2,
    lighter: NerdWalletGreen.LIGHTER,
    light: NerdWalletGreen.LIGHT,
    primary: NerdWalletGreen.PRIMARY,
    dark: NerdWalletGreen.DARK,
    darkest: NerdWalletGreen.DARKEST,
  },
  lime: {
    light: NerdWalletLime.LIGHT,
    primary: NerdWalletLime.PRIMARY,
    dark: NerdWalletLime.DARK,
  },
  yellow: {
    lightest: NerdWalletYellow.LIGHTEST,
    extraLight: NerdWalletYellow.EXTRA_LIGHT,
    lighter2: NerdWalletYellow.LIGHTER_2,
    lighter: NerdWalletYellow.LIGHTER,
    light: NerdWalletYellow.LIGHT,
    primary: NerdWalletYellow.PRIMARY,
    dark: NerdWalletYellow.DARK,
    darkest: NerdWalletYellow.DARKEST,
  },
  red: {
    lightest: NerdWalletRed.LIGHTEST,
    extraLight: NerdWalletRed.EXTRA_LIGHT,
    lighter2: NerdWalletRed.LIGHTER_2,
    lighter: NerdWalletRed.LIGHTER,
    light: NerdWalletRed.LIGHT,
    primary: NerdWalletRed.PRIMARY,
    dark: NerdWalletRed.DARK,
    darkest: NerdWalletRed.DARKEST,
  },
  brown: {
    lightest: NerdWalletBrown.LIGHTEST,
    extraLight: NerdWalletBrown.EXTRA_LIGHT,
    lighter2: NerdWalletBrown.LIGHTER_2,
    lighter: NerdWalletBrown.LIGHTER,
    light: NerdWalletBrown.LIGHT,
    primary: NerdWalletBrown.PRIMARY,
    dark: NerdWalletBrown.DARK,
    darkest: NerdWalletBrown.DARKEST,
  },
  neutral: {
    lightest: NerdWalletNeutral.LIGHTEST,
    lighter2: NerdWalletNeutral.LIGHTER_2,
    lighter: NerdWalletNeutral.LIGHTER,
    light: NerdWalletNeutral.LIGHT,
    dark: NerdWalletNeutral.DARK,
    darker: NerdWalletNeutral.DARKER,
    darker2: NerdWalletNeutral.DARKER_2,
    darkest: NerdWalletNeutral.DARKEST,
    bone: NerdWalletNeutral.BONE,
  },
  logo: {
    green: NerdWalletLogo.PRIMARY,
    dark: NerdWalletLogo.DARK,
    light: NerdWalletLogo.LIGHT,
  },
  common: {
    black: NerdWalletNeutral.DARKEST,
    white: NerdWalletNeutral.WHITE,
  },
});

export const theme = createTheme({
  palette,
  breakpoints: {
    values: {
      sm: 375,
      md: 768,
      lg: 1280,
    },
  },
  spacing: (factor: number) => `${0.25 * factor}rem`,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.common.white,
          color: palette.text.primary,
          boxShadow: '0 3px 10px #0000000d',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow:
            '0 4px 8px 0 rgba(100,102,106,0.1), 0 1px 4px 0 rgba(100,102,106,0.36)',
          borderRadius: '.5rem',
          border: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        body, input, select, button {
          font-size: 16px;
        }
        .visually-hidden {
          clip-path: inset(100%);
          clip: rect(1px, 1px, 1px, 1px);
          height: 1px;
          overflow: hidden;
          position: absolute;
          white-space: nowrap;
          width: 1px;
        }
        a {
          text-decoration: none;
          color: ${palette.blue.primary};
        }
        a:hover {
          text-decoration: underline;
        }
      `,
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: (
          <Box
            component={KeyboardArrowDownSharp}
            color="neutral.darkest"
            width={{ sm: 20, lg: 30 }}
            height={{ sm: 20, lg: 30 }}
          />
        ),
      },
      styleOverrides: {
        root: {
          '&:hover': {
            outlineWidth: 2,
            outlineStyle: 'solid',
            outlineColor: palette.blue.primary,
            outlineOffset: -1,
            backgroundColor: palette.blue.lightest,
            zIndex: 1,
          },
        },
      },
    },
    MuiBreadcrumbs: {
      defaultProps: {
        separator: '›',
      },
      styleOverrides: {
        li: {
          fontSize: 10,
          textTransform: 'uppercase',
          fontWeight: 700,
          '& .MuiIconButton-root': {
            color: palette.green.dark,
            '&:hover': {
              backgroundColor: palette.green.extraLight,
            },
          },
          '& > a': {
            color: palette.text.secondary,
            '&:hover': {
              color: palette.green.dark,
            },
          },
          '&:last-of-type': {
            color: palette.text.primary,
          },
        },
        separator: {
          marginLeft: 16,
          marginRight: 16,
          fontSize: 14,
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: 'blue' },
          style: {
            backgroundColor: palette.blue.lightest,
            color: palette.blue.light,
            '&:hover': {
              backgroundColor: palette.blue.lighter2,
            },
          },
        },
      ],
      defaultProps: {
        // options here
      },
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 600,
          '&.MuiChip-filledPrimary': {
            backgroundColor: palette.green.lightest,
            color: palette.green.dark,
            '&:hover': {
              backgroundColor: palette.green.lighter2,
            },
          },
          '&.MuiChip-colorDefault': {
            backgroundColor: 'white',
            color: palette.neutral.darker,
            '&:hover': {
              backgroundColor: palette.neutral.lightest,
            },
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          pointerEvents: 'auto',
          ':disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
            transition: 'none',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${palette.blue.light}`,
            outlineOffset: '4px',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
        disableFocusRipple: true,
        disableRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          border: 0,
          borderRadius: '0.125rem',
          display: 'inline-block',
          fontSize: '0.875rem',
          fontWeight: 700,
          letterSpacing: '0.11rem',
          lineHeight: 1.53846154,
          padding: '1rem',
          position: 'relative',
          textAlign: 'center',
          textTransform: 'uppercase',
          transitionDuration: '150ms',
          whiteSpace: 'nowrap',
          '&:active': {
            transitionDuration: '100ms',
            border: 0,
          },
          '&:hover:not(:disabled)': {
            border: 0,
          },
          '.MuiSvgIcon-root': {
            fontSize: 'inherit !important',
          },
          '&:disabled': {
            opacity: 0.5,
          },
        },
        contained: {
          lineHeight: 1,
          background: palette.primary.main,
          color: palette.common.white,
          '&:hover': {
            background: palette.green.dark,
          },
          '&:active': {
            background: palette.primary.main,
          },
          '&:disabled': {
            background: palette.primary.main,
            color: palette.common.white,
          },
          '@media (hover: none)': {
            '&:hover': {
              background: palette.primary.main,
            },
            '&:active': {
              background: palette.green.dark,
            },
          },
        },
        outlined: {
          lineHeight: 1,
          background: palette.common.white,
          color: palette.primary.main,
          boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
          '&:hover:not(:disabled)': {
            background: palette.common.white,
            color: palette.green.dark,
            boxShadow: `inset 0 0 0 2px ${palette.green.dark}`,
          },
          '&:active': {
            background: palette.common.white,
            color: palette.primary.main,
            boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
          },
          '&:disabled': {
            background: palette.common.white,
            color: palette.primary.main,
          },
          '@media (hover: none)': {
            '&:hover:not(:disabled)': {
              background: palette.common.white,
              color: palette.primary.main,
              boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
            },
            '&:active': {
              background: palette.common.white,
              color: palette.green.dark,
              boxShadow: `inset 0 0 0 2px ${palette.green.dark}`,
            },
          },
        },
      },
      variants: [
        {
          props: { variant: 'tertiary' },
          style: () => ({
            lineHeight: 1,
            background: palette.neutral.lightest,
            color: palette.primary.main,
            '&:hover:not(:disabled)': {
              background: palette.neutral.lighter,
            },
            '&:active': {
              background: palette.common.white,
            },
            '&:disabled': {
              background: palette.neutral.lightest,
              color: palette.primary.main,
            },
            '@media (hover: none)': {
              '&:hover': {
                background: palette.neutral.lightest,
              },
              '&:active': {
                background: palette.neutral.lighter,
              },
            },
          }),
        },
        {
          props: { variant: 'nwPlus' },
          style: () => ({
            lineHeight: 1,
            background: palette.green.dark,
            color: palette.lime.primary,
            '&:hover:not(:disabled)': {
              background: palette.green.primary,
            },
            '&:active': {
              background: palette.common.white,
            },
            '&:disabled': {
              background: palette.neutral.lightest,
              color: palette.primary.main,
            },
            '@media (hover: none)': {
              '&:hover': {
                background: palette.neutral.lightest,
              },
              '&:active': {
                background: palette.neutral.lighter,
              },
            },
          }),
        },
        {
          props: { variant: 'link' },
          style: () => ({
            color: `${palette.blue.primary}`,
            '&:hover': { 'text-decoration': 'underline' },
            '&:focus': {
              outline: 'none',
              'text-decoration': 'underline',
            },
          }),
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: 'white',
          width: '100%',
          borderRadius: '.125rem',
          '&:hover': {
            backgroundColor: palette.grey['100'],
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: palette.neutral.dark,
              borderWidth: '1px',
            },
          },
          '&.Mui-focused': {
            backgroundColor: palette.blue.lightest,
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: palette.blue.light,
              borderWidth: '2px',
            },
          },
          '&.Mui-error': {
            borderColor: palette.red.primary,
            backgroundColor: palette.red.extraLight,
            borderWidth: '1px',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: palette.red.dark,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'lg',
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: palette.secondary.main,
        },
        /*
          Setting a Link to component="button" removes our custom font,
          this is a MUI issue which is discussed here: https://github.com/mui/material-ui/issues/32942
        */
        button: {
          fontFamily: 'inherit',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1rem',
          '&:last-child': {
            paddingBottom: '1rem',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: NerdWalletNeutral.LIGHTER,
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        thickness: 2.5,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 16,
        },
        sizeSmall: {
          fontSize: 13,
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          '& .MuiRating-iconFilled': {
            color: palette.green.dark,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: 'inherit', // once we remove theme-ui, this can probably be deleted
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.grey['200']}`,
          borderTop: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: palette.grey['100'],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.grey['200']}`,
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&:hover, :focus': {
            color: 'inherit',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: 'inherit',
          },
          '&.Mui-error': {
            color: palette.red.primary,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          border: `1px solid ${palette.neutral.lighter}`,
          backgroundColor: palette.common.white,
          borderRadius: 0,
          textAlign: 'left',
          color: palette.text.primary,
          fontSize: '.8125rem',
          fontWeight: 'normal',
          lineHeight: '1.53846154',
          boxShadow:
            '0 4px 8px 0 rgb(100 102 106 / 15%), 0 8px 16px 0 rgb(100 102 106 / 33%)',
          padding: '0.5rem 1rem 0.5rem 1rem !important',
        },
        arrow: {
          fontSize: '22px',
          '&::before': {
            border: `1px solid ${palette.neutral.lighter}`,
            backgroundColor: palette.common.white,
          },
        },
        tooltipPlacementBottom: {
          margin: '16px !important',
          marginTop: '12px !important',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: palette.blue.light,
          '&.Mui-checked': {
            color: palette.blue.light,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: palette.blue.lightest,
            '&:active, &:hover, &:focus': {
              backgroundColor: palette.blue.lightest,
            },
          },
        },
      },
    },
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: FontFamily.GOTHAM,
    fontSize: 14,
    fontWeightLight: 100,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 500,
    allVariants: {
      WebkitFontSmoothing: 'antialiased',
    },
    title: {
      fontSize: '32px',
      fontWeight: 400,
      lineHeight: '111%',
      fontFamily: FontFamily.CHRONICLE_DISPLAY,
      letterSpacing: '0.1px',
      [breakpoints.up('md')]: {
        fontSize: '48px',
        lineHeight: '53px',
      },
    },
    titleLarge: {
      fontSize: '32px',
      fontWeight: 400,
      lineHeight: '111%',
      fontFamily: FontFamily.GOTHAM,
      letterSpacing: '0.1px',
      [breakpoints.up('md')]: {
        fontSize: '48px',
        lineHeight: '53px',
      },
    },
    titleExtraLarge: {
      fontSize: '32px',
      fontWeight: 400,
      lineHeight: '111%',
      fontFamily: FontFamily.CHRONICLE_DISPLAY,
      letterSpacing: '0.1px',
      [breakpoints.up('md')]: {
        fontSize: '54px',
        lineHeight: '53px',
      },
    },
    titleSmall: {
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: '117%',
      fontFamily: FontFamily.CHRONICLE_DISPLAY,
      letterSpacing: '0.0025rem',
      [breakpoints.up('md')]: {
        fontSize: '36px',
        lineHeight: '42px',
      },
    },
    textExtraSmall: {
      fontFamily: FontFamily.GOTHAM,
      fontSize: '0.688rem',
      lineHeight: 1.5,
      letterSpacing: '0.04rem',
    },
    textSmall: {
      fontFamily: FontFamily.GOTHAM,
      fontSize: 13,
      lineHeight: 1.4,
      letterSpacing: '0.03rem',
    },
    text: {
      fontFamily: FontFamily.GOTHAM,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.005rem',
    },
    textLarge: {
      fontFamily: FontFamily.GOTHAM,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '0.005rem',
    },
    accent: {
      fontFamily: FontFamily.GOTHAM,
      fontSize: '0.875rem',
      fontWeight: 700,
      letterSpacing: '0.11rem',
      lineHeight: 0.9,
      textTransform: 'uppercase',
    },
    accentSmall: {
      fontFamily: FontFamily.GOTHAM,
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.09rem',
      lineHeight: 1.1,
      textTransform: 'uppercase',
    },
  },
  shadows: [
    'none',
    '0 4px 8px 0 rgba(100,102,106,0.1), 0 1px 4px 0 rgba(100,102,106,0.36)',
    '0 4px 8px 0 rgba(100,102,106,0.15), 0 8px 16px 0 rgba(100,102,106,0.33)',
    '0 32px 40px 0 rgba(100,102,106,0.33), 0 8px 16px 0 rgba(100,102,106,0.15)',
    ...defaultShadows.slice(4),
  ] as any,
});

export const ThemeProvider = ({
  merge,
  ...props
}: {
  merge?: Partial<any>;
  children?: ReactNode;
}) => <MuiThemeProvider theme={deepmerge(theme, merge)} {...props} />;
